import { Button, Dialog, HStack, IconButton, Portal } from '@chakra-ui/react'
import { useState } from 'react'
import { FiX } from 'react-icons/fi'

/**
 * Modal Component (migrated to Dialog in Chakra v3)
 * Wrapper around Chakra UI Dialog
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  isCentered = true,
  closeOnOverlayClick = true,
  ...props
}) {
  return (
    <Dialog.Root
      open={ isOpen }
      onOpenChange={ (details) => !details.open && onClose() }
      size={ size }
      placement={ isCentered ? 'center' : 'top' }
      closeOnInteractOutside={ closeOnOverlayClick }
      { ...props }
    >
      <Portal>
        <Dialog.Backdrop bg="rgba(9, 30, 66, 0.54)" />
        <Dialog.Positioner>
          <Dialog.Content
            bg="white"
            borderRadius="3px"
            boxShadow="0 8px 16px 0 rgba(9, 30, 66, 0.16), 0 0 1px 0 rgba(9, 30, 66, 0.24)"
            p={ 0 }
            maxH="90vh"
            display="flex"
            flexDirection="column"
          >
            { title && (
              <Dialog.Header
                fontSize="20px"
                fontWeight="500"
                color="#172B4D"
                p={ 6 }
                pb={ 3 }
                flexShrink={ 0 }
              >
                { title }
              </Dialog.Header>
            ) }
            <Dialog.CloseTrigger asChild>
              <IconButton
                position="absolute"
                top="12px"
                right="12px"
                variant="ghost"
                size="sm"
                aria-label="Close"
                zIndex={ 10 }
                bg="white"
                _hover={{ bg: "#F4F5F7" }}
              >
                <FiX size="20px" />
              </IconButton>
            </Dialog.CloseTrigger>
            <Dialog.Body
              px={ 6 }
              py={ 3 }
              overflowY="auto"
              flexGrow={ 1 }
            >
              { children }
            </Dialog.Body>
            { footer && (
              <Dialog.Footer
                display="flex"
                justifyContent="flex-end"
                gap={ 2 }
                p={ 6 }
                pt={ 4 }
                flexShrink={ 0 }
                borderTop="1px solid #DFE1E6"
              >
                { footer }
              </Dialog.Footer>
            ) }
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

// Export Dialog sub-components for compound pattern usage
Modal.Header = Dialog.Header
Modal.Body = Dialog.Body
Modal.Footer = Dialog.Footer
Modal.CloseTrigger = Dialog.CloseTrigger

/**
 * Hook for managing modal state (useDisclosure replacement)
 */
export function useDisclosure(defaultIsOpen = false) {
  const [isOpen, setIsOpen] = useState(defaultIsOpen)

  const onOpen = () => setIsOpen(true)
  const onClose = () => setIsOpen(false)
  const onToggle = () => setIsOpen((prev) => !prev)

  return { isOpen, onOpen, onClose, onToggle }
}

/**
 * Confirmation Modal
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  colorScheme = 'red',
}) {
  const handleConfirm = async () => {
    await onConfirm?.()
    onClose()
  }

  return (
    <Dialog.Root
      open={ isOpen }
      onOpenChange={ (details) => !details.open && onClose() }
      size="md"
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop bg="rgba(9, 30, 66, 0.54)" />
        <Dialog.Positioner>
          <Dialog.Content
            bg="white"
            borderRadius="3px"
            boxShadow="0 8px 16px 0 rgba(9, 30, 66, 0.16), 0 0 1px 0 rgba(9, 30, 66, 0.24)"
            maxW="400px"
            p={ 0 }
          >
            <Dialog.Header
              fontSize="20px"
              fontWeight="500"
              color="#172B4D"
              p={ 6 }
              pb={ 3 }
            >
              { title }
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <IconButton
                position="absolute"
                top="12px"
                right="12px"
                variant="ghost"
                size="sm"
                aria-label="Close"
              >
                <FiX size="20px" />
              </IconButton>
            </Dialog.CloseTrigger>
            <Dialog.Body
              fontSize="14px"
              color="#172B4D"
              lineHeight="1.5"
              px={ 6 }
              py={ 3 }
            >
              { message }
            </Dialog.Body>
            <Dialog.Footer
              p={ 6 }
              pt={ 4 }
            >
              <HStack spacing={ 3 } justify="flex-end">
                <Button
                  variant="secondary"
                  size="md"
                  px={ 4 }
                  onClick={ onClose }
                  disabled={ isLoading }
                >
                  { cancelText }
                </Button>
                <Button
                  size="md"
                  px={ 4 }
                  bg={ colorScheme === 'red' ? '#DE350B' : '#0052CC' }
                  color="white"
                  _hover={{ bg: colorScheme === 'red' ? '#BF2600' : '#0747A6' }}
                  onClick={ handleConfirm }
                  loading={ isLoading }
                >
                  { confirmText }
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default Modal
